'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { DonutChart } from './DonutChart';
import { SectionContainer, SectionHeader, FilterChip, MaskedText, Button } from '@/components/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { InfoPopover } from '@/components/ui/InfoPopover';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import {
  IconInfo,
  IconHomeChimney,
  IconChevronRight,
  IconAlertTriangle,
  IconAlertTriangleCheck,
} from '@repo/svgs';
import { cn } from '@/lib/cn';

import { getViolationHistoryByYear } from '@repo/api';
import { useStore } from '@/providers/StoreProvider';
import { useBuilding } from '../../context/BuildingProvider';
import { ViolationHistorySectionSkeleton } from './ViolationHistorySectionSkeleton';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toSentenceCase, toTitleCase } from '@/utils/textTransform';

interface UnitViolation {
  unit: string;
  floor: number | string;
  violationCount: number;
}

interface ViolationHistoryResponse {
  last_3year_violations: {
    A: number;
    B: number;
    C: number;
    I: number;
  };
  unit_violations: Array<{
    unit: string;
    floor: string | number;
    class_A: Array<{ year: number; approved_date: string; current_status: string }>;
    class_B: Array<{ year: number; approved_date: string; current_status: string }>;
    class_C: Array<{ year: number; approved_date: string; current_status: string }>;
    class_I: Array<{ year: number; approved_date: string; current_status: string }>;
  }>;
}

interface ViolationHistorySectionProps {
  buildingId: string;
  buildingSlug: string;
  buildingAddress: string;

  // If report isn't ready, you said treat it like blur required
  isBlurRequired?: boolean;

  // block year fetch until add-report flow completes
  waitForAddReport?: boolean;

  // NEW: base fetch comes from parent now
  baseViolationData: ViolationHistoryResponse | any | null;
  baseLoading: boolean;
  baseError: string | null;
}

// Generate year filters: last 4 years including current year, plus "All"
const getYearFilters = () => {
  const currentYear = new Date().getFullYear();
  const years = ['All'];
  for (let i = 0; i < 4; i++) years.push(String(currentYear - i));
  return years;
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString || typeof dateString !== 'string') return 'Invalid Date';
  try {
    const trimmedDate = dateString.trim();
    const date = new Date(trimmedDate);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return 'Invalid Date';
  }
};

interface Violation {
  approvedDate?: string;
  currentStatus?: string;
  novDescription?: string;
  violationStatus?: string;
  year: number;
}

const ViolationClassSection = ({
  title,
  violations,
  color,
}: {
  title: string;
  violations: Violation[];
  color: string;
}) => {
  if (!violations || violations.length === 0) return null;

  return (
    <div>
      <div className="px-2 py-3">
        <h3 className="text-body-2-high text-primary">{title}</h3>
      </div>
      <div>
        {violations.map((violation, index, array) => (
          <div key={index}>
            <div className="flex items-start gap-x-4 py-3 px-2">
              {violation.violationStatus === 'Open' ? (
                <IconAlertTriangle className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
              ) : (
                <IconAlertTriangleCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
              )}
              <div className="flex-1 min-w-0">
                {violation.currentStatus && (
                  <span className="text-body-2 text-primary pb-1">
                    {toTitleCase(violation.currentStatus)}
                  </span>
                )}
                <div className="flex items-center text-caption text-secondary pb-2">
                  {violation.approvedDate && (
                    <span>
                      {formatDate(violation.approvedDate)} ·{' '}
                      {violation.violationStatus?.toLowerCase() === 'close'
                        ? 'Closed'
                        : violation.violationStatus}
                    </span>
                  )}
                </div>
                {violation.novDescription && (
                  <p className="text-caption text-secondary lowercase">
                    {toSentenceCase(violation.novDescription)}
                  </p>
                )}
              </div>
            </div>
            {index < array.length - 1 && <div className="border-b border-tertiary my-1 mx-2" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export function ViolationHistorySection({
  buildingId,
  buildingSlug,
  buildingAddress,
  isBlurRequired = false,
  waitForAddReport = false,
  baseViolationData,
  baseLoading,
  baseError,
}: ViolationHistorySectionProps) {
  const { activeTab } = useBuilding();
  const analytics = useAnalytics();

  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const explorerData = useStore(state => state.explorer.data);

  const [selectedYear, setSelectedYear] = useState('All');
  const [isDesktop, setIsDesktop] = useState(false);
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);

  const [yearViolationData, setYearViolationData] = useState<ViolationHistoryResponse | any | null>(
    null,
  );
  const [yearDataCache, setYearDataCache] = useState<Record<string, any>>({});
  const [loadingYearData, setLoadingYearData] = useState(false);
  const [yearError, setYearError] = useState<string | null>(null);

  const infoIconRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setIsDesktop(window.innerWidth >= 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const yearFilters = getYearFilters();

  // (Optional) stable values from explorerData to avoid extra renders
  useMemo(() => {
    if (!explorerData) return null;
    const accessBuildingsSlugs =
      explorerData.accessBuildingsData?.map((b: any) => b.slug).filter(Boolean) || [];
    const freeAccessSlugs =
      explorerData.freeAccessBuildings?.map((b: any) => b.slug).filter(Boolean) || [];
    const hasAccess =
      accessBuildingsSlugs.includes(buildingSlug) || freeAccessSlugs.includes(buildingSlug);
    return { hasAccess };
  }, [explorerData, buildingSlug]);

  // Transform API response to component format
  const transformViolationData = (data: any) => {
    const violationsData = data.violations?.last3yearViolations || {};
    const violations = {
      classC: violationsData.c || 0,
      classB: violationsData.b || 0,
      classA: violationsData.a || 0,
      classI: violationsData.i || 0,
    };

    const total = violations.classA + violations.classB + violations.classC + violations.classI;

    const unitViolationsData =
      data.violations?.unit_violations ||
      data.unit_violations ||
      data.violations?.unitViolations ||
      data.unitViolations ||
      [];

    const units: UnitViolation[] = (
      Array.isArray(unitViolationsData) ? unitViolationsData : []
    ).map((unit: any) => {
      const violationCount =
        (unit.classA?.length || 0) +
        (unit.classB?.length || 0) +
        (unit.classC?.length || 0) +
        (unit.classI?.length || 0);

      return {
        unit: unit.unit || 'Unknown',
        floor: unit.floor || 'Unknown',
        violationCount,
      };
    });

    return { violations, total, units, totalUnits: units.length };
  };

  const getFilteredUnits = (data: any | null, year: string): UnitViolation[] => {
    if (!data) return [];

    if (year === 'All') {
      return transformViolationData(data).units;
    }

    const unitViolationsData = data.unitViolations || data.violations?.unitViolations;
    if (!unitViolationsData || !Array.isArray(unitViolationsData)) return [];

    const yearNum = parseInt(year, 10);

    const filteredUnits = unitViolationsData
      .filter((unit: any) => {
        const allViolations = [
          ...(unit.class_A || unit.classA || []),
          ...(unit.class_B || unit.classB || []),
          ...(unit.class_C || unit.classC || []),
          ...(unit.class_I || unit.classI || []),
        ];
        return allViolations.some((v: any) => v.year === yearNum);
      })
      .map((unit: any) => {
        const yearViolations = [
          ...((unit.class_A || unit.classA)?.filter((v: any) => v.year === yearNum) || []),
          ...((unit.class_B || unit.classB)?.filter((v: any) => v.year === yearNum) || []),
          ...((unit.class_C || unit.classC)?.filter((v: any) => v.year === yearNum) || []),
          ...((unit.class_I || unit.classI)?.filter((v: any) => v.year === yearNum) || []),
        ];

        return {
          unit: unit.unit || 'Unknown',
          floor: unit.floor || 'Unknown',
          violationCount: yearViolations.length,
        };
      });

    return filteredUnits;
  };

  // Fetch year-specific data when year filter changes
  useEffect(() => {
    // prevent any calls when not on report tab
    if (activeTab !== 'report') return;

    // block year fetch until base exists and report ready
    if (waitForAddReport) return;

    // Only fetch year data if user is logged in and blur is not required
    if (!isLoggedIn || isBlurRequired || selectedYear === 'All' || !baseViolationData) {
      setYearViolationData(null);
      setYearError(null);
      return;
    }

    // cache hit
    if (yearDataCache[selectedYear]) {
      setYearViolationData(yearDataCache[selectedYear]);
      return;
    }

    const fetchYearData = async () => {
      setLoadingYearData(true);
      setYearError(null);

      try {
        const response = (await getViolationHistoryByYear({
          buildingId,
          year: selectedYear,
        })) as ViolationHistoryResponse;

        setYearDataCache(prev => ({ ...prev, [selectedYear]: response }));
        setYearViolationData(response);
      } catch (err: any) {
        setYearError(err?.message || 'Failed to load violation history');
      } finally {
        setLoadingYearData(false);
      }
    };

    void fetchYearData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, buildingId, selectedYear, isLoggedIn, isBlurRequired, baseViolationData]);

  // base transformed data (chart always from base)
  const transformedData = baseViolationData ? transformViolationData(baseViolationData) : null;

  // For units list: year-specific data if available, else base
  const unitsDataForFilter =
    selectedYear === 'All' ? baseViolationData : yearViolationData || baseViolationData;

  const displayUnits = unitsDataForFilter ? getFilteredUnits(unitsDataForFilter, selectedYear) : [];

  const getFullUnitData = (unitNumber: string, floorNumber: string | number): any | null => {
    if (!unitsDataForFilter) return null;

    let unitViolationsData: any[] = [];

    if (selectedYear === 'All') {
      unitViolationsData =
        unitsDataForFilter.violations?.unit_violations ||
        unitsDataForFilter.unit_violations ||
        unitsDataForFilter.violations?.unitViolations ||
        [];
    } else {
      unitViolationsData =
        unitsDataForFilter.unit_violations ||
        unitsDataForFilter.violations?.unit_violations ||
        unitsDataForFilter.unitViolations ||
        unitsDataForFilter.violations?.unitViolations ||
        [];
    }

    const unit = unitViolationsData.find(
      (u: any) => u.unit === unitNumber && String(u.floor) === String(floorNumber),
    );

    if (!unit) return null;

    const transformViolation = (v: any) => ({
      approvedDate: v.approved_date || v.approvedDate,
      currentStatus: v.current_status || v.currentStatus,
      novDescription: v.nov_description || v.novDescription,
      violationStatus: v.violation_status || v.violationStatus,
      year: v.year,
    });

    if (selectedYear !== 'All') {
      const yearNum = parseInt(selectedYear, 10);
      return {
        unit: unit.unit,
        floor: unit.floor,
        classA: (unit.class_A || unit.classA || [])
          .filter((v: any) => v.year === yearNum)
          .map(transformViolation),
        classB: (unit.class_B || unit.classB || [])
          .filter((v: any) => v.year === yearNum)
          .map(transformViolation),
        classC: (unit.class_C || unit.classC || [])
          .filter((v: any) => v.year === yearNum)
          .map(transformViolation),
        classI: (unit.class_I || unit.classI || [])
          .filter((v: any) => v.year === yearNum)
          .map(transformViolation),
      };
    }

    return {
      unit: unit.unit,
      floor: unit.floor,
      classA: (unit.class_A || unit.classA || []).map(transformViolation),
      classB: (unit.class_B || unit.classB || []).map(transformViolation),
      classC: (unit.class_C || unit.classC || []).map(transformViolation),
      classI: (unit.class_I || unit.classI || []).map(transformViolation),
    };
  };

  const getUnitViolationContent = (unit: UnitViolation) => (close: () => void) => {
    if (isBlurRequired) return null;
    const fullUnitData = getFullUnitData(unit.unit, unit.floor);
    if (!fullUnitData) return null;

    const allViolations = [
      ...(fullUnitData.classC || []),
      ...(fullUnitData.classB || []),
      ...(fullUnitData.classA || []),
      ...(fullUnitData.classI || []),
    ];

    if (allViolations.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-body-2 text-secondary">
            No violations found for this unit{selectedYear !== 'All' ? ` in ${selectedYear}` : ''}.
          </p>
        </div>
      );
    }

    return (
      <div>
        <ViolationClassSection
          title="Class C violations (Immediately hazardous)"
          violations={fullUnitData.classC || []}
          color="#EF4444"
        />
        <ViolationClassSection
          title="Class B violations (Hazardous)"
          violations={fullUnitData.classB || []}
          color="#DC2626"
        />
        <ViolationClassSection
          title="Class A violations (Non-hazardous)"
          violations={fullUnitData.classA || []}
          color="#92400E"
        />
        <ViolationClassSection
          title="Class I violations (Missing info)"
          violations={fullUnitData.classI || []}
          color="#A16207"
        />
      </div>
    );
  };

  const [selectedAllUnitsUnit, setSelectedAllUnitsUnit] = useState<UnitViolation | null>(null);

  const closeAllUnitsModal = () => {
    setSelectedAllUnitsUnit(null);
  };

  const allUnitsModalLabel = selectedAllUnitsUnit
    ? `Violation history - unit #${selectedAllUnitsUnit.unit} on floor ${selectedAllUnitsUnit.floor}`
    : 'All units with violation history';

  const chartData = transformedData
    ? [
        {
          label: 'Class C · Immediately hazardous',
          value: transformedData.violations.classC,
          color: '#EF4444',
        },
        {
          label: 'Class B · Hazardous',
          value: transformedData.violations.classB,
          color: '#DC2626',
        },
        {
          label: 'Class A · Non-hazardous',
          value: transformedData.violations.classA,
          color: '#92400E',
        },
        {
          label: 'Class I · Missing info',
          value: transformedData.violations.classI,
          color: '#A16207',
        },
      ].filter(item => item.value > 0)
    : [];

  const total = transformedData?.total || 0;

  // keep your existing behavior
  if (isLoggedIn && explorerData === null) return null;

  // base fetch state comes from parent
  if (baseLoading) return <ViolationHistorySectionSkeleton />;

  const combinedError = baseError || yearError;
  if (combinedError) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-body-2 text-alert">{combinedError}</p>
      </div>
    );
  }

  return (
    <div>
      <SectionHeader
        title={`Violation history for ${buildingAddress}`}
        trailingIcon={
          isDesktop ? (
            <>
              <button
                ref={infoIconRef}
                type="button"
                aria-label="About violation history"
                className="inline-flex"
                onClick={() => {
                  analytics.track('rentalreport_violationhistory_tooltip');
                  setIsInfoPopoverOpen(true);
                }}
              >
                <IconInfo className="w-4 h-4 icon-primary cursor-pointer" />
              </button>
              <InfoPopover
                open={isInfoPopoverOpen}
                onOpenChange={setIsInfoPopoverOpen}
                onClose={() => setIsInfoPopoverOpen(false)}
                label="About violation history"
                anchorRef={infoIconRef}
                placement="below"
                align="right"
                offsetY={32}
                offsetX={16}
              >
                <div className="flex flex-col gap-4">
                  <p className="text-body-2 text-primary">
                    Violations get issued when a city inspector visits a building. City inspectors
                    commonly visit buildings when a complaint is filed with 311.
                  </p>
                </div>
              </InfoPopover>
            </>
          ) : (
            <ModalTrigger
              label="About violation history"
              content={
                <div className="flex flex-col gap-4">
                  <p className="text-body-2 text-primary">
                    Violations get issued when a city inspector visits a building. City inspectors
                    commonly visit buildings when a complaint is filed with 311.
                  </p>
                </div>
              }
              urlKey="about-violation-history"
              maxWidth="md"
              onOpen={() => analytics.track('rentalreport_violationhistory_tooltip')}
            >
              <IconInfo className="w-4 h-4 icon-primary cursor-pointer" />
            </ModalTrigger>
          )
        }
        subtitleText="City data from past 3 years · All open and closed violations per unit"
      />

      {chartData.length > 0 && total > 0 ? (
        <div className="flex flex-col sm:flex-row gap-8 items-start py-6 px-4">
          <DonutChart data={chartData} total={total} isBlurRequired={isBlurRequired} />
        </div>
      ) : baseViolationData ? (
        <div className="md:mx-4 mx-2">
          <p className="text-body-2 text-primary">
            {buildingAddress} does not have any violations from the past 3 years.
          </p>
        </div>
      ) : null}

      {/* Hide "per unit" section entirely when there are no units with violations (non-blur). */}
      {isBlurRequired || (chartData.length > 0 && total > 0) ? (
        <SectionContainer className="md:pl-2">
          <div className="px-2 py-3">
            <h3 className="text-body-2-high text-primary">Violation history per unit</h3>
          </div>

          <div className="flex gap-2 mb-2 flex-wrap pl-2">
            {yearFilters.map(year => (
              <FilterChip
                key={year}
                active={selectedYear === year}
                label={year}
                onClick={() => {
                  if (year === 'All') analytics.track('rentalreport_violationhistory_allyears');
                  else analytics.track('rentalreport_violationhistory_yearfilter', { year });
                  setSelectedYear(year);
                }}
                showTrailingIcon={false}
                disabled={isBlurRequired}
              />
            ))}
          </div>

          {isBlurRequired ? (
            <div className="pl-2">
              <MaskedText className="h-4 !mb-0" />
            </div>
          ) : (
            <p className="text-body-2 text-secondary pl-2">
              This building had {displayUnits?.length ?? 0} units with violation history{' '}
              {selectedYear === 'All' ? 'from the past 3 years' : `in ${selectedYear}`}.
            </p>
          )}

          {loadingYearData ? (
            <div>
              {[1, 2].map(i => (
                <div key={i} className={cn(i < 2 && 'border-b border-tertiary my-1')}>
                  <div className="flex items-center gap-x-4 py-3 px-2">
                    <Skeleton className="w-6 h-6 rounded shrink-0" />
                    <div className="flex-1">
                      <Skeleton className="h-5 mb-2 w-40" />
                      <Skeleton className="h-4 mt-1 w-20" />
                    </div>
                    <Skeleton className="w-4 h-4 rounded shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              {displayUnits.slice(0, 2).map((unit, index, array) => (
                <div key={index}>
                  {isBlurRequired ? (
                    <div className="flex items-center gap-x-4 py-3 px-2">
                      <IconHomeChimney className="w-6 h-6 text-secondary shrink-0" />
                      <div className="flex-1">
                        <MaskedText className="h-5 mb-2 w-40" />
                        <MaskedText className="h-4 mt-1 w-20" />
                      </div>
                    </div>
                  ) : (
                    <ModalTrigger
                      label={`Violation history - unit #${unit.unit} on floor ${unit.floor}`}
                      content={getUnitViolationContent(unit)}
                      maxWidth="md"
                      onOpen={() =>
                        analytics.track('rentalreport_violationhistory_unitview', {
                          unit: String(unit.unit),
                          floor: String(unit.floor),
                        })
                      }
                      onClose={() => analytics.track('rentalreport_violationhistory_unitview_exit')}
                    >
                      <div className="flex items-center gap-x-4 py-3 px-2 cursor-pointer">
                        <IconHomeChimney className="w-6 h-6 text-secondary shrink-0" />
                        <div className="flex-1">
                          <div className="text-body-2 text-primary">
                            Unit #{unit?.unit ?? 'unknown'} on floor {unit?.floor ?? 'unknown'}
                          </div>
                          <div className="text-caption text-secondary pt-1">
                            {unit?.violationCount}{' '}
                            {unit?.violationCount === 1 ? 'violation' : 'violations'}
                          </div>
                        </div>
                        <IconChevronRight className="w-4 h-4 text-secondary shrink-0" />
                      </div>
                    </ModalTrigger>
                  )}
                  {index < array.length - 1 && (
                    <div className="border-b border-tertiary my-1 mx-2" />
                  )}
                </div>
              ))}
            </div>
          )}

          {!loadingYearData && displayUnits.length > 2 && (
            <ModalTrigger
              label={allUnitsModalLabel}
              maxWidth="md"
              disabled={isBlurRequired}
              onOpen={() => analytics.track('rentalreport_violationhistory_expand')}
              onClose={() => {
                analytics.track('rentalreport_violationhistory_collapse');
                closeAllUnitsModal();
              }}
              {...(selectedAllUnitsUnit
                ? {
                    onBack: () => {
                      analytics.track('rentalreport_violationhistory_unitview_collapse');
                      setSelectedAllUnitsUnit(null);
                    },
                  }
                : {})}
              content={() => {
                if (displayUnits.length === 0) {
                  return (
                    <div className="flex items-center justify-center py-12">
                      <p className="text-body-2 text-secondary">No units with violations found.</p>
                    </div>
                  );
                }

                if (selectedAllUnitsUnit) {
                  return getUnitViolationContent(selectedAllUnitsUnit)(closeAllUnitsModal);
                }

                return (
                  <div>
                    {displayUnits.map((unit, index) => (
                      <div
                        key={index}
                        className={cn(
                          index < displayUnits.length - 1 && 'border-b border-tertiary',
                        )}
                      >
                        <div
                          className={cn(
                            'flex items-center gap-x-4 py-3 px-2',
                            !isBlurRequired && 'cursor-pointer hover:bg-primary-hover',
                          )}
                          onClick={() => {
                            if (isBlurRequired) return;
                            analytics.track('rentalreport_violationhistory_unitview_expand');
                            analytics.track('rentalreport_violationhistory_unitview', {
                              unit: String(unit.unit),
                              floor: String(unit.floor),
                            });
                            setSelectedAllUnitsUnit(unit);
                          }}
                        >
                          <div className="flex-1">
                            <div className="text-body-2 text-primary">
                              Unit #{unit?.unit ?? 'unknown'} on floor {unit?.floor ?? 'unknown'}
                            </div>
                            <div className="text-caption text-secondary pt-1">
                              {unit?.violationCount}{' '}
                              {unit?.violationCount === 1 ? 'violation' : 'violations'}
                            </div>
                          </div>
                          <IconChevronRight className="w-4 h-4 text-secondary shrink-0" />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }}
            >
              <Button variant="secondary" className="md:w-fit mx-2">
                View all units
              </Button>
            </ModalTrigger>
          )}
        </SectionContainer>
      ) : null}
    </div>
  );
}
